import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { fetchTenants, logout, type Tenant } from '../api/portal';
import { useUser } from '../routes/app/AuthGate';
import logo from '../assets/logo.png';
import '../styles/help-admin.css';

// Admin chrome matching the Digadop Help admin portal (React-Client
// AdminNav.js; rules ported verbatim in styles/help-admin.css). The product
// frame is ONE Digadop customer: their tenants live in the header dropdown
// (Help's account-swap pattern), and every tab scopes to the selected tenant.
// Operators see all tenants through the same dropdown.

const TenantContext = createContext<Tenant | null>(null);
/** The tenant every admin tab scopes to; null until tenants load (show all). */
// eslint-disable-next-line react-refresh/only-export-components -- hook co-located with its provider component
export const useTenant = () => useContext(TenantContext);

const TENANT_STORAGE = 'digadop-ai-tenant-slug';

const TABS: Array<{ to: string; label: string; match: (path: string) => boolean }> = [
  { to: '/app/runs', label: 'Recent Runs', match: (p) => p === '/app' || p.startsWith('/app/runs') },
  {
    to: '/app/documents',
    label: 'Documents',
    match: (p) => p.startsWith('/app/documents') || p.startsWith('/app/review'),
  },
  {
    to: '/app/release-notes',
    label: 'Release Notes',
    match: (p) => p.startsWith('/app/release-notes'),
  },
  { to: '/app/personas', label: 'Personas', match: (p) => p.startsWith('/app/personas') },
];

export function AdminShell({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const user = useUser();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchTenants()
      .then(({ tenants: list }) => {
        setTenants(list);
        const saved = localStorage.getItem(TENANT_STORAGE);
        setTenant(list.find((t) => t.slug === saved) ?? list[0] ?? null);
      })
      .catch(() => setTenants([]));
  }, []);

  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const pickTenant = (t: Tenant) => {
    localStorage.setItem(TENANT_STORAGE, t.slug);
    setTenant(t);
    setMenuOpen(false);
  };

  return (
    <div className="help-admin min-h-screen bg-white">
      <div className="px-[10%] py-4 flex items-center justify-between min-h-[60px]">
        <Link to="/app/runs" className="flex items-center gap-2">
          <img src={logo} alt="Digadop" className="h-[30px]" />
          <span className="text-sm font-semibold text-slate-500 tracking-wide mt-1">AI</span>
        </Link>
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="flex items-center gap-2 text-sm text-slate-800"
          >
            {tenant && <span className="font-medium">{tenant.display_name}</span>}
            {tenant && <span className="text-slate-300">|</span>}
            <span>{user?.name}</span>
            <span className="text-slate-400 text-xs">▾</span>
          </button>
          {menuOpen && (
            <div className="dropdown-menu">
              {tenants.map((t) => (
                <button key={t.id} className="dropdown-item" onClick={() => pickTenant(t)}>
                  <span className="inline-block w-4 text-[#2a94d6]">
                    {tenant?.id === t.id ? '✓' : ''}
                  </span>
                  {t.display_name}
                  <span className="block pl-4 text-xs text-slate-400">{t.slug}</span>
                </button>
              ))}
              <button
                className="dropdown-item dropdown-signout text-right"
                onClick={() => {
                  logout().finally(() => window.location.reload());
                }}
              >
                <span className="signout-label">Sign Out</span>
                <br />
                <small className="text-slate-400">({user?.name})</small>
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="px-[10%]">
        <ul className="nav-tabs">
          {TABS.map((tab) => {
            const active = tab.match(pathname);
            return (
              <li key={tab.to} className={active ? 'active' : ''}>
                <Link to={tab.to}>{tab.label}</Link>
              </li>
            );
          })}
        </ul>
        <hr className="border-slate-200 m-0" />
      </div>

      {/* The shell owns the horizontal rhythm: content shares the tabs' 10%
          gutter instead of centering itself to a different axis. */}
      <main className="px-[10%]">
        <TenantContext.Provider value={tenant}>{children}</TenantContext.Provider>
      </main>
    </div>
  );
}
